import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import Tabs from '@/components/Tabs';

/**
 * ── /components/tabs ─────────────────────────────────────────────────────
 *
 * Tabs — the segmented-control molecule behind home's "All experiences"
 * filter (`app/index.tsx`, fed by `EXPERIENCE_FILTERS` in
 * `src/hooks/useAllExperiences.ts`: today / tomorrow / this weekend). It's
 * the ONLY real call site in the app — generic on `options`/`value`/
 * `onChange` so it isn't tied to that one filter enum, but nothing else
 * currently reaches for it.
 *
 * Covers: the live tappable demo mirroring home's actual 3-option filter;
 * how the control behaves as option COUNT grows (segments are always
 * equal-width via `grow={1}` on each cell — there's no scroll, no wrap, the
 * track just partitions itself into more, narrower slices); and the
 * `numberOfLines={1}` label clip that kicks in once a segment gets too
 * narrow for its text, which only shows up once options are numerous or a
 * label runs long — home's own 3 short labels never trigger it.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Mirrors home's real EXPERIENCE_FILTERS (useAllExperiences.ts) — same 3
// keys/labels, defined locally so this preview doesn't couple to that
// hook's query-paging concerns, just the shape Tabs actually consumes.
const HOME_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekend', label: 'This Weekend' },
] as const;

// A denser fixture than any real screen uses — six short filters — purely
// to show the track keeps partitioning into equal slices rather than
// scrolling or wrapping to a second row. `grow={1}` on every cell means
// each one simply gets narrower as the sibling count climbs.
const MANY_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekend', label: 'Weekend' },
  { key: 'week', label: 'This Week' },
  { key: 'next', label: 'Next Week' },
  { key: 'all', label: 'All Time' },
] as const;

// One deliberately long label among short ones — forces that one segment's
// `numberOfLines={1}` text to ellipsize instead of wrapping or pushing its
// neighbors wider (every segment is equal-width regardless of its own
// label length, so a long label can't claim extra room from the others).
const LONG_LABEL_FILTERS = [
  { key: 'a', label: 'All' },
  { key: 'b', label: 'Live Music & Comedy Nights' },
  { key: 'c', label: 'Art' },
] as const;

export default function TabsPreview() {
  // Drives the live demo below — mirrors home's `experiences.filter` state,
  // one selected tab at a time among home's real 3 options.
  const [homeFilter, setHomeFilter] =
    useState<(typeof HOME_FILTERS)[number]['key']>('today');

  return (
    <>
      <Head>
        <title>Tabs · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Live — tap a segment to select it. Exact fixture home's "All
            experiences" section renders: 3 day filters, one always
            selected, switching redraws the grid below it (out of scope
            here — this preview only owns the control). */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap to switch (home&apos;s &quot;All experiences&quot;
              filter: today / tomorrow / this weekend)
            </Text>
            <Tabs
              options={HOME_FILTERS}
              value={homeFilter}
              onChange={setHomeFilter}
            />
          </YStack>

          {/* Two options vs six — same track, same `alignSelf="stretch"`
            width, but each cell's share shrinks as sibling count grows.
            There's no scroll or overflow menu built in: everything the
            caller passes has to fit across one row, partitioned evenly. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              two options — each segment claims half the track
            </Text>
            <Tabs
              options={HOME_FILTERS.slice(0, 2)}
              value="today"
              onChange={() => {}}
            />
          </YStack>
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              six options — denser than any real screen currently shows; the
              track keeps splitting evenly rather than scrolling or wrapping to
              a second row
            </Text>
            <Tabs options={MANY_FILTERS} value="week" onChange={() => {}} />
          </YStack>

          {/* Long label — every segment is equal-width off sibling COUNT
            alone, never off its own label's length, so a long label can't
            borrow room from its neighbors. Once it runs out of space the
            label clips to one line with an ellipsis (`numberOfLines={1}`
            in Tabs.tsx) instead of wrapping the segment taller. */}
          <YStack gap="$xs" alignSelf="stretch">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              long label — clips to one line with an ellipsis instead of
              wrapping or stealing width from its neighbors
            </Text>
            <Tabs options={LONG_LABEL_FILTERS} value="b" onChange={() => {}} />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
